import { AlertTriangle, Loader2, PlugZap, ShieldAlert, ShieldCheck } from 'lucide-react'
import { useCallback, useMemo, useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { CONSENT_COPY } from '@/features/oauth-consent/consentCopy'
import {
  ConsentApiError,
  signIn,
  submitConsent,
  type ConsentDecision,
  type ConsentRequestParams,
} from '@/features/oauth-consent/services/oauthConsentService.http'

/**
 * `/oauth/consent` - the sign-in + consent screen for the PX OAuth 2.1
 * authorization server (PX-W4.8, ported from xelerate-ai's
 * `features/oauth-consent` with decision D7's inline credential step).
 *
 * The browser arrives here because `GET /api/v1/oauth/authorize` validated the
 * authorization request and answered 303 to this route, carrying the validated
 * params plus the SERVER-TRUSTED client display:
 *
 *   /oauth/consent?client_id=...&redirect_uri=...&scope=mcp&resource=...
 *                 &code_challenge=...&code_challenge_method=S256
 *                 &response_type=code&client_name=...&verified=true|false&state=...
 *
 * Everything rendered here is server-supplied: `client_name` and `verified`
 * come from the registered client row, resolved by the AS before it redirected
 * (never echoed from attacker-supplied input), and React renders them as text -
 * no markup is ever built from a query value.
 *
 * Flow:
 *   no token in memory -> the inline sign-in step (D7)
 *   missing params     -> the invalid-request panel, which is also the empty state
 *   signed in + valid  -> client name, requested scope, Allow / Deny
 *   Allow -> POST consent `approve` -> full-page hop to the returned redirect
 *   Deny  -> POST consent `deny`    -> the AS's `access_denied` redirect
 *
 * PX's own `/login` is deliberately NOT used for this screen: the consent flow
 * needs its credential step inline (D7), and the shared transport now carries
 * the Bearer for the consent POST from the auth service's in-memory store.
 *
 * The access token lives in the auth service's module memory (W6.1 migration
 * onto the shared client) - never localStorage, never sessionStorage, never a
 * URL, and never component state. The password lives in one `useState` and is
 * cleared the moment sign-in succeeds.
 */

/** Which half of the surface is showing. */
type Step = 'sign-in' | 'consent'

/** In-flight/settled state of either submit. */
type Phase = 'idle' | 'submitting' | 'error'

/** The single coarse scope the MCP requests in v1. */
const MCP_SCOPE = 'mcp'

export default function OAuthConsentPage() {
  const [searchParams] = useSearchParams()

  // The AS-resolved display name (FF2 on the backend) - a registered-client
  // field, not user input.
  const clientName = searchParams.get('client_name') ?? ''

  const params = useMemo<ConsentRequestParams>(
    () => ({
      clientId: searchParams.get('client_id') ?? '',
      redirectUri: searchParams.get('redirect_uri') ?? '',
      scope: searchParams.get('scope') ?? MCP_SCOPE,
      state: searchParams.get('state') ?? '',
      codeChallenge: searchParams.get('code_challenge') ?? '',
      resource: searchParams.get('resource') ?? '',
    }),
    [searchParams]
  )

  // A well-formed request carries what the backend needs to mint a code: the
  // client, where to send the user back, the PKCE challenge and the audience.
  // `state` is optional for some clients, so it is not part of the gate.
  const hasValidRequest =
    params.clientId.length > 0 &&
    params.redirectUri.length > 0 &&
    params.codeChallenge.length > 0 &&
    params.resource.length > 0

  // Every client reaching this screen self-registered through open Dynamic
  // Client Registration, so an unvetted third party is the default: warn unless
  // the AS explicitly marked the row verified.
  const isUnverifiedClient = searchParams.get('verified') !== 'true'

  const [step, setStep] = useState<Step>('sign-in')

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [signInPhase, setSignInPhase] = useState<Phase>('idle')
  const [signInError, setSignInError] = useState('')

  const [consentPhase, setConsentPhase] = useState<Phase>('idle')
  const [pendingDecision, setPendingDecision] = useState<ConsentDecision | null>(null)

  const onEmailChanged = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
    setSignInError('')
  }, [])

  const onPasswordChanged = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value)
    setSignInError('')
  }, [])

  const onSignInSubmitted = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      if (email.trim().length === 0 || password.length === 0) {
        setSignInPhase('error')
        setSignInError(CONSENT_COPY.signIn.missingFields)
        return
      }
      setSignInPhase('submitting')
      setSignInError('')
      try {
        await signIn(email.trim(), password)
        // The credential has done its job; drop it before anything renders again.
        setPassword('')
        setSignInPhase('idle')
        setStep('consent')
      } catch (error) {
        setSignInPhase('error')
        setSignInError(signInMessageFor(error))
      }
    },
    [email, password]
  )

  const submitDecision = useCallback(
    async (decision: ConsentDecision) => {
      setPendingDecision(decision)
      setConsentPhase('submitting')
      try {
        const { redirect } = await submitConsent(decision, params)
        // The destination is the external OAuth client, not an in-app route, so
        // a real browser navigation is correct here - `navigate()` would not
        // leave the SPA.
        window.location.assign(redirect)
      } catch {
        setConsentPhase('error')
        setPendingDecision(null)
      }
    },
    [params]
  )

  const onAllowClicked = useCallback(() => {
    // `approve` is the backend's fail-closed wire enum; "Allow access" is only
    // the button label.
    void submitDecision('approve')
  }, [submitDecision])

  const onDenyClicked = useCallback(() => {
    void submitDecision('deny')
  }, [submitDecision])

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12 text-foreground">
      <Card className="w-full max-w-md">
        {!hasValidRequest ? (
          <InvalidRequestPanel />
        ) : step === 'sign-in' ? (
          <>
            <CardHeader className="items-center text-center">
              <ShieldCheck className="h-7 w-7 text-primary" aria-hidden />
              <CardTitle>{CONSENT_COPY.signIn.heading}</CardTitle>
              <CardDescription>{CONSENT_COPY.signIn.lede}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={onSignInSubmitted} noValidate className="grid gap-3">
                <div className="grid gap-1">
                  <label
                    htmlFor="oauth-consent-email"
                    className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
                  >
                    {CONSENT_COPY.signIn.emailLabel}
                  </label>
                  <Input
                    id="oauth-consent-email"
                    name="email"
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={onEmailChanged}
                  />
                </div>
                <div className="grid gap-1">
                  <label
                    htmlFor="oauth-consent-password"
                    className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
                  >
                    {CONSENT_COPY.signIn.passwordLabel}
                  </label>
                  <Input
                    id="oauth-consent-password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={onPasswordChanged}
                  />
                </div>

                {signInPhase === 'error' && signInError !== '' && (
                  <p
                    role="alert"
                    className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive"
                  >
                    {signInError}
                  </p>
                )}

                <Button type="submit" className="w-full" disabled={signInPhase === 'submitting'}>
                  {signInPhase === 'submitting' ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      {CONSENT_COPY.signIn.submitting}
                    </>
                  ) : (
                    CONSENT_COPY.signIn.submit
                  )}
                </Button>
              </form>
            </CardContent>
          </>
        ) : (
          <>
            <CardHeader className="items-center text-center">
              <PlugZap className="h-7 w-7 text-primary" aria-hidden />
              <CardTitle>
                {CONSENT_COPY.title(clientName || CONSENT_COPY.fallbackClient)}
              </CardTitle>
              <CardDescription>{CONSENT_COPY.subtitle}</CardDescription>
            </CardHeader>

            <CardContent className="grid gap-4">
              <div className="rounded-md border border-border p-4">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {CONSENT_COPY.scope.title}
                    </p>
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      {CONSENT_COPY.scope.body}
                    </p>
                  </div>
                </div>
              </div>

              {/* Advisory, shown on load: role="note", so the submit failure
                  below stays this page's single live alert region. */}
              {isUnverifiedClient && (
                <div
                  role="note"
                  className="flex items-start gap-3 rounded-md border border-border bg-muted p-3"
                >
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-blocked" aria-hidden />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {CONSENT_COPY.unverified.title}
                    </p>
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      {CONSENT_COPY.unverified.body}
                    </p>
                  </div>
                </div>
              )}

              <p className="text-xs text-muted-foreground">{CONSENT_COPY.reassurance}</p>

              {consentPhase === 'error' && (
                <p
                  role="alert"
                  className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive"
                >
                  {CONSENT_COPY.error}
                </p>
              )}

              <div className="grid gap-2">
                <Button
                  type="button"
                  size="lg"
                  className="w-full"
                  disabled={consentPhase === 'submitting'}
                  aria-busy={consentPhase === 'submitting' && pendingDecision === 'approve'}
                  onClick={onAllowClicked}
                >
                  {consentPhase === 'submitting' && pendingDecision === 'approve' ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      {CONSENT_COPY.submitting}
                    </>
                  ) : (
                    CONSENT_COPY.allow
                  )}
                </Button>
                <Button
                  type="button"
                  size="lg"
                  variant="outline"
                  className="w-full"
                  disabled={consentPhase === 'submitting'}
                  aria-busy={consentPhase === 'submitting' && pendingDecision === 'deny'}
                  onClick={onDenyClicked}
                >
                  {consentPhase === 'submitting' && pendingDecision === 'deny' ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      {CONSENT_COPY.submitting}
                    </>
                  ) : (
                    CONSENT_COPY.deny
                  )}
                </Button>
              </div>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  )
}

/**
 * Map a sign-in failure to the copy the user sees. Only the reason travels -
 * the backend's own message is never rendered, so nothing it echoes can reach
 * the page.
 */
function signInMessageFor(error: unknown): string {
  if (error instanceof ConsentApiError) {
    if (error.reason === 'invalid-credentials') return CONSENT_COPY.signIn.invalidCredentials
    if (error.reason === 'two-factor-required') return CONSENT_COPY.signIn.twoFactor
  }
  return CONSENT_COPY.signIn.failed
}

/**
 * Shown when the consent URL lacks the params the backend needs - a malformed
 * link, an expired one, or someone typing `/oauth/consent` directly. Doubles as
 * the empty state: there is no request to consent to.
 */
function InvalidRequestPanel() {
  return (
    <CardHeader className="items-center text-center">
      <ShieldAlert className="h-7 w-7 text-blocked" aria-hidden />
      <CardTitle>{CONSENT_COPY.invalid.title}</CardTitle>
      <CardDescription>{CONSENT_COPY.invalid.body}</CardDescription>
    </CardHeader>
  )
}
